import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import {
  Button,
  Input,
  Select,
  BUTTON_THEME,
  CircularLoader,
  Icon,
} from '@st-operations/uikit/src/v2/index.js'

import './styles.scss'

import { getDaasClients } from '_services/daas-clients'

import { useEventFeedsContext } from '../../context/index.jsx'
import { getTaxonomyRequiredErrors } from '../../query-panel/utils.js'
import CollapsibleSection from '_components/collapsible-section/index.jsx'
import DeliveryStatus from './delivery-status/index.jsx'
import FieldValidationMessage from './field-validation-message/index.jsx'
import FileConfigurationForm from './file-configuration-form/index.jsx'
import DestinationConfigurationForm from './destination-configuration-form/index.jsx'
import DeliveryFrequencyForm from './delivery-frequency-form/index.jsx'
import HemProviderForm from './hem-provider-form/index.jsx'
import GenerateS3KeysButton from './generate-s3-keys-button/index.jsx'
import S3AccessKeys from './s3-access-keys/index.jsx'
import useDebouncedStateCommit from '../../../../hooks/useDebouncedStateCommit'
import DataSourceForm from './data-source-form'

const DELIVERY_STATUS = {
  PROCESSED: 'processed',
  SUBMITTED: 'submitted',
  DRAFT: 'draft',
  ARCHIVED: 'archived',
}

const DeliveryModal = ({ onClose, onSave }) => {
  const {
    data,
    data: { deliveries, attributes },
    dispatch,
    ACTIONS,
  } = useEventFeedsContext()
  const delivery = deliveries[0] ?? {}

  const [clients, setClients] = useState([])

  const [requiredFields, setRequiredFields] = useState({})

  const checkRequiredFields = () => {
    const partnerS3 = delivery.destination === 'partner-s3'
    const shareThisS3 = delivery.destination === 'sharethis-s3'
    const s3CrossAccount = delivery.destination === 's3-cross-account'
    const s3AccessMethod = delivery.s3AccessMethod

    const rf = {
      feedName: !data.name?.trim().length > 0,
      client: !data.client,
      frequency: !delivery.frequency,
      frequencyFrom: !delivery.sampleStartDate && delivery.frequency === 'one-time',
      frequencyTo: !delivery.sampleEndDate && delivery.frequency === 'one-time',
      fileFormat: !delivery.format,
      fileCompression: !delivery.compression,
      partitionBy: !delivery.partitionType,
      destination: !delivery.destination,
      name: !delivery.name?.trim().length > 0,
      s3AccessMethod: (partnerS3 || shareThisS3 || s3CrossAccount) && !s3AccessMethod,
      s3Path:
        (partnerS3 || shareThisS3 || s3CrossAccount) &&
        ['partner-iam-role', 'sharethis-iam-role', 's3-access-key'].includes(
          s3AccessMethod || ''
        ) &&
        !delivery.s3Path,
      remoteS3Path:
        (s3AccessMethod === 'partner-iam-role' ||
          (s3AccessMethod === 's3-access-key' && (partnerS3 || s3CrossAccount))) &&
        !delivery.remoteS3Path?.trim().length > 0,
      s3AccessKeyId:
        s3AccessMethod === 's3-access-key' &&
        (partnerS3 || s3CrossAccount) &&
        !delivery.s3AccessKeyId?.trim().length > 0,
      s3SecretAccessKey:
        s3AccessMethod === 's3-access-key' &&
        (partnerS3 || s3CrossAccount) &&
        !delivery.s3SecretAccessKey?.trim().length > 0,
      partnerAwsPrincipal:
        (s3AccessMethod === 'partner-iam-role' || s3AccessMethod === 'sharethis-iam-role') &&
        !delivery.partnerAwsPrincipal?.trim().length > 0,
      credentialName:
        (s3AccessMethod === 'partner-iam-role' || s3AccessMethod === 'sharethis-iam-role') &&
        !delivery.credentialName?.trim().length > 0,
      sharethisRoleArn:
        (s3AccessMethod === 'partner-iam-role' || s3AccessMethod === 'sharethis-iam-role') &&
        !delivery.sharethisRoleArn?.trim().length > 0,
      sharethisIamRoleExternalId:
        (s3AccessMethod === 'partner-iam-role' || s3AccessMethod === 'sharethis-iam-role') &&
        !delivery.sharethisIamRoleExternalId?.trim().length > 0,
      apiEndpoint: delivery.destination === 'api' && !delivery.apiEndpoint,
      apiKey: delivery.destination === 'api' && !delivery.apiKey,
      apiSecretKey: delivery.destination === 'api' && !delivery.apiSecretKey,
      gcsProject: delivery.destination === 'gcs' && !delivery.gcsProject,
      gcsRemotePath: delivery.destination === 'gcs' && !delivery.gcsRemotePath,
      gcsServiceAccount: delivery.destination === 'gcs' && !delivery.gcsServiceAccount,
      sftpHost: delivery.destination === 'sftp' && !delivery.sftpHost,
      sftpPassword: delivery.destination === 'sftp' && !delivery.sftpPassword,
      sftpPort: delivery.destination === 'sftp' && !delivery.sftpPort,
      sftpRemotePath: delivery.destination === 'sftp' && !delivery.sftpRemotePath,
      sftpUsername: delivery.destination === 'sftp' && !delivery.sftpUsername,
      // Custom Taxonomy dependent validations. Shared with the page-level check so
      // the Custom Label fields stop being required as soon as its toggle is off.
      ...getTaxonomyRequiredErrors(data),
    }
    setRequiredFields(rf)

    const result = Object.values(rf).some((s) => s)
    if (result) {
      toast.error('There are some required field that must be filled')
    }

    return result
  }

  const resetRequiredFields = () => {
    setRequiredFields({})
  }

  useEffect(() => {
    async function getClients() {
      try {
        const response = await getDaasClients()
        setClients(response.items)
      } catch (error) {
        console.error(error.message)
        toast.error(error.message)
      }
    }

    getClients()
  }, [])

  const handleClientSelectChange = (value) => {
    dispatch({
      type: ACTIONS.UPDATE_CLIENT,
      payload: value,
    })
  }

  const handleSaveAsDraft = () => {
    resetRequiredFields()
    onSave()?.then(() => onClose())
  }

  const [loadingSave, setLoadingSave] = useState(false)
  const didMountDeliveryStatus = useRef(undefined)
  useEffect(() => {
    if (didMountDeliveryStatus.current) {
      if (delivery?.status !== DELIVERY_STATUS.DRAFT) {
        setLoadingSave(true)
        onSave()
          .then(() => {
            onClose()
            setLoadingSave(false)
          })
          .catch(() => {
            dispatch({
              type: ACTIONS.UPDATE_DELIVERY,
              payload: {
                status: didMountDeliveryStatus.current,
              },
            })
            setLoadingSave(false)
          })
      }
    } else {
      didMountDeliveryStatus.current = delivery?.status
    }
  }, [delivery?.status])

  const handleSubmit = () => {
    if (checkRequiredFields()) {
      return
    }
    dispatch({
      type: ACTIONS.UPDATE_DELIVERY,
      payload: {
        status: DELIVERY_STATUS.SUBMITTED,
      },
    })
  }

  const handleArchive = () => {
    dispatch({
      type: ACTIONS.UPDATE_DELIVERY,
      payload: {
        status: DELIVERY_STATUS.ARCHIVED,
      },
    })
  }

  const [feedName, setFeedName] = useDebouncedStateCommit(data.name, () => { })

  const [generatingS3Keys, setGeneratingS3Keys] = useState(false)
  const onGenerateS3KeysClick = () => {
    setGeneratingS3Keys(true)

    let s3AccessKeys = JSON.parse(JSON.stringify(delivery?.s3AccessKeys || []))
    s3AccessKeys = []
    s3AccessKeys.push({ name: delivery.name })

    dispatch({
      type: ACTIONS.UPDATE_DELIVERY,
      payload: {
        s3AccessKeys,
      },
    })
  }

  useEffect(() => {
    if (generatingS3Keys) {
      setGeneratingS3Keys(false)
      setLoadingSave(true)
      onSave().then(() => {
        setLoadingSave(false)
      })
      return
    }
  }, [delivery?.s3AccessKeys])

  return (
    <section
      className={classNames('delivery-modal', {
        'delivery-modal--with-warning': attributes?.length === 0,
      })}
    >
      <DeliveryStatus className="delivery-content__status" />
      <div className="delivery-content__content">
        <div className="delivery-content__content-general">
          <div className="delivery-content__content-column">
            <Input
              className={classNames('delivery-content__content-no-margin', {
                'delivery-content__required-field': requiredFields.feedName,
              })}
              value={feedName}
              onChange={setFeedName}
              name="name"
              placeholder="Feed name"
              label="Feed Name"
            />
            <FieldValidationMessage fieldName="Feed Name" required={requiredFields.feedName} />
          </div>
          <div className="delivery-content__field-container">
            <Select
              className={classNames('delivery-content__content-no-margin', {
                'delivery-content__required-field': requiredFields.client,
              })}
              value={data.client}
              options={clients}
              name="client"
              label="Client"
              onChange={handleClientSelectChange}
              isSearchable
              useReactSelect
            />
            <FieldValidationMessage
              fieldName="Client"
              className="delivery-content__required--client"
              required={requiredFields.client}
            />
          </div>
        </div>
        <div className="delivery-content__content-section">
          <CollapsibleSection
            title={<h3 className="delivery-content__content-title">Delivery Frequency</h3>}
            description="How often data is delivered"
          >
            <DeliveryFrequencyForm
              requiredErrorFrequency={requiredFields.frequency}
              requiredErrorFrequencyFrom={requiredFields.frequencyFrom}
              requiredErrorFrequencyTo={requiredFields.frequencyTo}
            />
          </CollapsibleSection>
        </div>
        <div className="delivery-content__content-section">
          <CollapsibleSection
            title={<h3 className="delivery-content__content-title">Data Sources</h3>}
          >
            <DataSourceForm />
          </CollapsibleSection>
        </div>
        <div className="delivery-content__content-section">
          <CollapsibleSection
            title={<h3 className="delivery-content__content-title">Hem Providers</h3>}
          >
            <HemProviderForm />
          </CollapsibleSection>
        </div>
        <div className="delivery-content__content-section">
          <CollapsibleSection
            title={<h3 className="delivery-content__content-title">File Configuration</h3>}
          >
            <FileConfigurationForm
              requiredErrorFileFormat={requiredFields.fileFormat}
              requiredErrorFileCompression={requiredFields.fileCompression}
              requiredErrorFilePartitionBy={requiredFields.partitionBy}
            />
          </CollapsibleSection>
        </div>
        <S3AccessKeys />
        <div className="delivery-content__content-section">
          <CollapsibleSection
            title={<h3 className="delivery-content__content-title">Destination Configuration</h3>}
          >
            <DestinationConfigurationForm
              requiredErrorDestination={requiredFields.destination}
              requiredErrorS3AccessMethod={requiredFields.s3AccessMethod}
              requiredErrorBucket={requiredFields.s3Path}
              requiredErrorDestinationName={requiredFields.name}
              requiredErrorRemoteS3Path={requiredFields.remoteS3Path}
              requiredErrorS3AccessKeyId={requiredFields.s3AccessKeyId}
              requiredErrorS3SecretAccessKeyId={requiredFields.s3SecretAccessKey}
              requiredErrorPartnerAwsPrinciple={requiredFields.partnerAwsPrincipal}
              requiredErrorCredentialName={requiredFields.credentialName}
              requiredErrorSharethisRoleArn={requiredFields.sharethisRoleArn}
              requiredErrorSharethisIamRoleExternalId={requiredFields.sharethisIamRoleExternalId}
              requiredErrorEndpoint={requiredFields.apiEndpoint}
              requiredErrorKey={requiredFields.apiKey}
              requiredErrorSecret={requiredFields.apiSecretKey}
              requiredErrorProject={requiredFields.gcsProject}
              requiredErrorRemotePath={requiredFields.gcsRemotePath}
              requiredErrorServiceAccount={requiredFields.gcsServiceAccount}
              requiredErrorHost={requiredFields.sftpHost}
              requiredErrorPassword={requiredFields.sftpPassword}
              requiredErrorPort={requiredFields.sftpPort}
              requiredErrorSftpRemotePath={requiredFields.sftpRemotePath}
              requiredErrorSftpUsername={requiredFields.sftpUsername}
            />
          </CollapsibleSection>
        </div>
      </div>
      <If condition={attributes?.length === 0}>
        <div className="delivery-content__warning">
          <Icon type="fill" icon="error" className="delivery-content__warning-icon" />
          You must add attributes to this feed in order to submit a delivery
        </div>
      </If>
      <footer className="delivery-content__footer">
        <Choose>
          <When condition={loadingSave}>
            <CircularLoader className="delivery-content__loading" />
          </When>
          <When condition={delivery?.status === DELIVERY_STATUS.DRAFT}>
            <Button
              theme={BUTTON_THEME.SECONDARY}
              className="delivery-content__footer-button"
              onClick={handleSaveAsDraft}
            >
              Save as Draft
            </Button>
            <Button className="delivery-content__footer-button" onClick={handleSubmit}>
              Submit
            </Button>
            <GenerateS3KeysButton onClick={onGenerateS3KeysClick} />
          </When>
          <When condition={delivery?.status === DELIVERY_STATUS.ARCHIVED}>
            <Button
              theme={BUTTON_THEME.SECONDARY}
              className="delivery-content__footer-button"
              onClick={handleSaveAsDraft}
            >
              Save Changes
            </Button>
            <Button className="delivery-content__footer-button" onClick={handleSubmit}>
              Submit
            </Button>
            <GenerateS3KeysButton onClick={onGenerateS3KeysClick} />
          </When>
          <Otherwise>
            <Button
              theme={BUTTON_THEME.SECONDARY}
              className="delivery-content__footer-button"
              onClick={handleSaveAsDraft}
            >
              Save Changes
            </Button>
            <Button className="delivery-content__footer-button" onClick={handleArchive}>
              Archive
            </Button>
            <GenerateS3KeysButton onClick={onGenerateS3KeysClick} />
          </Otherwise>
        </Choose>
      </footer>
    </section>
  )
}

DeliveryModal.propTypes = {
  id: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
}

DeliveryModal.defaultProps = {
  id: '',
  isOpen: false,
  onClose: () => { },
}

export default React.memo(DeliveryModal)
